import React from 'react'
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import { useIntl } from 'react-intl'
import {
  AtsConnectorField,
  AtsConnectorSchema,
  AtsFieldError,
  AtsFormValues,
  isJsonField,
  isRowsField,
  localize,
  orderedFields,
} from './atsConnectorSchema'

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }

const errorMessages: Record<AtsFieldError, { id: string; defaultMessage: string }> = {
  required: { id: 'ats-field-required', defaultMessage: 'Required' },
  url: { id: 'ats-field-invalid-url', defaultMessage: 'Enter a full URL, e.g. https://…' },
  pattern: { id: 'ats-field-invalid-format', defaultMessage: 'Invalid format' },
  number: { id: 'ats-field-invalid-number', defaultMessage: 'Enter a number' },
  json: { id: 'ats-field-invalid-json', defaultMessage: 'Enter valid JSON' },
}

interface AtsConfigFieldsProps {
  schema: AtsConnectorSchema
  values: AtsFormValues
  errors: Record<string, AtsFieldError>
  onChange: (values: AtsFormValues) => void
}

/** Renders a TalentBridge connector's config schema as form fields. */
const AtsConfigFields = ({ schema, values, errors, onChange }: AtsConfigFieldsProps) => {
  const intl = useIntl()
  const required = schema.required ?? []

  // 'required' is not shown inline: the submit button stays disabled and the field is
  // starred. Format errors are shown as soon as there is something to complain about.
  const helperFor = (errorKey: string) => {
    const error = errors[errorKey]
    return error && error !== 'required' ? intl.formatMessage(errorMessages[error]) : undefined
  }

  const renderInput = (
    key: string,
    field: AtsConnectorField,
    value: string,
    isRequired: boolean,
    errorKey: string,
    setValue: (value: string) => void
  ) => {
    const label = localize(field.label, intl.locale, key)
    const helperText = helperFor(errorKey)
    const common = {
      fullWidth: true,
      label,
      value,
      required: isRequired,
      variant: 'outlined' as const,
      size: 'small' as const,
      error: !!helperText,
      helperText,
      sx: inputSx,
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value),
    }

    if (field.enum) {
      return (
        <TextField {...common} select>
          {field.enum.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      )
    }
    if (isJsonField(field)) {
      return <TextField {...common} multiline minRows={3} placeholder="{ }" />
    }
    if (field.format === 'password') {
      return <TextField {...common} type="password" autoComplete="new-password" />
    }
    if (field.type === 'number') {
      return <TextField {...common} type="number" />
    }
    return <TextField {...common} type={field.format === 'url' ? 'url' : 'text'} />
  }

  const renderRows = (key: string, field: AtsConnectorField) => {
    const itemSchema = field.items as AtsConnectorSchema
    const rows = Array.isArray(values[key]) ? (values[key] as Record<string, string>[]) : []
    const setRows = (next: Record<string, string>[]) => onChange({ ...values, [key]: next })
    const itemFields = orderedFields(itemSchema)

    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          {localize(field.label, intl.locale, key)}
          {required.includes(key) ? ' *' : ''}
        </Typography>
        {rows.map((row, index) => (
          // Rows have no stable id and are only ever appended or removed.
          // eslint-disable-next-line react/no-array-index-key
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
            {itemFields.map(([subKey, subField]) => (
              <Box key={subKey} sx={{ flex: 1 }}>
                {renderInput(
                  subKey,
                  subField,
                  row[subKey] ?? '',
                  (itemSchema.required ?? []).includes(subKey),
                  `${key}.${index}.${subKey}`,
                  (value) =>
                    setRows(rows.map((r, i) => (i === index ? { ...r, [subKey]: value } : r)))
                )}
              </Box>
            ))}
            <Tooltip title={intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' })}>
              <IconButton
                size="small"
                sx={{ mt: 0.5 }}
                onClick={() => setRows(rows.filter((_, i) => i !== index))}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none' }}
          onClick={() =>
            setRows([...rows, Object.fromEntries(itemFields.map(([subKey]) => [subKey, '']))])
          }
        >
          {intl.formatMessage({ id: 'ats-add-row', defaultMessage: 'Add entry' })}
        </Button>
      </Box>
    )
  }

  return (
    <Grid container spacing={2}>
      {orderedFields(schema).map(([key, field]) => {
        let input: React.ReactNode
        if (isRowsField(field)) {
          input = renderRows(key, field)
        } else if (field.type === 'boolean') {
          input = (
            <FormControlLabel
              control={
                <Switch
                  checked={values[key] === true}
                  onChange={(event) => onChange({ ...values, [key]: event.target.checked })}
                />
              }
              label={localize(field.label, intl.locale, key)}
            />
          )
        } else {
          input = renderInput(
            key,
            field,
            typeof values[key] === 'string' ? (values[key] as string) : '',
            required.includes(key),
            key,
            (value) => onChange({ ...values, [key]: value })
          )
        }
        return (
          <Grid item xs={12} key={key}>
            {input}
          </Grid>
        )
      })}
    </Grid>
  )
}

export default AtsConfigFields
